if (!customElements.get('product-add-to-cart-sticky')) {
class ProductAddToCartSticky extends HTMLElement {
    constructor() {
        super();
        this.handleScroll = this.handleScroll.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.updatePricing = this.updatePricing.bind(this);
        this.sectionId = this.dataset.section;
        this.scrollThreshold = 200; // Show after scrolling 200px
    }

    connectedCallback() {
        this.targetElement = document.querySelector('[id^="ProductSubmitButton-"]');
        this.footerElement = document.querySelector('footer');
        this.stickyAtcVariantInput = this.querySelector('.product-variant-id');
        
        // Price elements
        this.priceElements = {
            current: this.querySelector('.sticky-price-current'),
            compare: this.querySelector('.sticky-price-compare'),
            discount: this.querySelector('.sticky-price-discount'),
            stockIndicator: this.querySelector('.stock-indicator'),
            stockText: this.querySelector('.stock-text')
        };

        // Button elements
        this.submitButton = this.querySelector('.sticky-add-button');
        this.buttonIcon = this.querySelector('.sticky-add-icon');

        window.addEventListener("scroll", this.handleScroll);
        document.body.addEventListener('change', this.handleVariantChange);

        // Subscribe to variant change events if using pub/sub
        if (window.subscribe) {
            window.subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                if (event.data.sectionId === this.sectionId) {
                    this.updateFromVariant(event.data.variant);
                }
            });
        }

        // Initial scroll check
        this.handleScroll();
    }

    disconnectedCallback() {
        window.removeEventListener("scroll", this.handleScroll);
        document.body.removeEventListener('change', this.handleVariantChange);
    }

    getAbsoluteTop(element) {
        if (!element) return 0;
        let top = 0;
        while (element) {
            top += element.offsetTop || 0;
            element = element.offsetParent;
        }
        return top;
    }

    handleScroll() {
        const scrollPosition = window.scrollY;
        const targetTopPosition = this.getAbsoluteTop(this.targetElement);
        const footerTopPosition = this.getAbsoluteTop(this.footerElement);
        const windowHeight = window.innerHeight;
        
        // Show if scrolled past threshold and target element is out of view
        const shouldShow = scrollPosition > this.scrollThreshold && 
                          (targetTopPosition < scrollPosition || targetTopPosition > scrollPosition + windowHeight);
        
        // Hide if footer is in view
        const isFooterInView = scrollPosition + windowHeight > footerTopPosition - 100;

        if (shouldShow && !isFooterInView) {
            this.classList.add('show');
        } else {
            this.classList.remove('show');
        }
    }

    updateStickyATCOption(optionName, optionValue) {
        const matches = optionName.match(/options\[(.+)\]/);
        if (matches && matches[1]) {
            optionName = matches[1];
        }

        // Update variant display
        const variantElement = this.querySelector(`.variant-value[data-option-name="${optionName}"]`);
        if (variantElement) {
            variantElement.textContent = optionValue;
        }
    }

    handleVariantChange(event) {
        const target = event.target;
        const productSection = target.closest(`#MainProduct-${this.sectionId}`);
        if (!productSection) return;
        
        if (target.tagName === 'INPUT' || target.tagName === 'SELECT') {
            const optionName = target.name;
            const optionValue = target.tagName === 'INPUT' ? target.value : target.options[target.selectedIndex].textContent;
            this.updateStickyATCOption(optionName, optionValue);
        }
    }

    updateFromVariant(variant) {
        if (!variant) return;

        // Update variant ID
        if (this.stickyAtcVariantInput) {
            this.stickyAtcVariantInput.value = variant.id;
        }

        // Update pricing
        this.updatePricing(variant);

        // Update stock status
        this.updateStockStatus(variant.available);

        // Update button state
        this.updateButtonState(variant.available);

        // Update image if available
        if (variant.featured_media) {
            this.updateImage(variant.featured_media);
        }
    }

    updatePricing(variant) {
        if (!this.priceElements.current) return;

        // Update current price
        this.priceElements.current.textContent = this.formatMoney(variant.price);

        // Update compare price and discount
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            // Show compare price
            if (this.priceElements.compare) {
                this.priceElements.compare.textContent = this.formatMoney(variant.compare_at_price);
                this.priceElements.compare.style.display = '';
            }

            // Calculate and show discount
            if (this.priceElements.discount) {
                const discountAmount = variant.compare_at_price - variant.price;
                const discountPercentage = Math.round((discountAmount / variant.compare_at_price) * 100);
                this.priceElements.discount.textContent = `-${discountPercentage}%`;
                this.priceElements.discount.style.display = '';
            }
        } else {
            // Hide compare price and discount
            if (this.priceElements.compare) this.priceElements.compare.style.display = 'none';
            if (this.priceElements.discount) this.priceElements.discount.style.display = 'none';
        }
    }

    updateStockStatus(available) {
        if (!this.priceElements.stockIndicator || !this.priceElements.stockText) return;

        if (available) {
            this.priceElements.stockIndicator.classList.remove('out-of-stock');
            this.priceElements.stockIndicator.classList.add('in-stock');
            this.priceElements.stockText.textContent = window.variantStrings?.inStock || 'In stock';
        } else {
            this.priceElements.stockIndicator.classList.remove('in-stock');
            this.priceElements.stockIndicator.classList.add('out-of-stock');
            this.priceElements.stockText.textContent = window.variantStrings?.soldOut || 'Out of stock';
        }
    }

    updateButtonState(available) {
        if (!this.submitButton) return;

        this.submitButton.disabled = !available;
        
        // Update icon based on availability
        if (this.buttonIcon) {
            if (available) {
                this.buttonIcon.innerHTML = `
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                `;
            } else {
                this.buttonIcon.innerHTML = `
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                `;
            }
        }

        // Update aria-label
        const label = available ? 
            (window.variantStrings?.addToCart || 'Add to cart') : 
            (window.variantStrings?.soldOut || 'Sold out');
        this.submitButton.setAttribute('aria-label', label);
    }

    updateImage(featuredMedia) {
        const imageElement = this.querySelector('.sticky-product-image img');
        if (imageElement && featuredMedia.preview_image) {
            imageElement.src = featuredMedia.preview_image.src;
            imageElement.srcset = featuredMedia.preview_image.srcset || '';
        }
    }

    formatMoney(cents) {
        // Get the money format from theme settings
        const moneyFormat = window.theme?.settings?.money_with_currency_format || "₹{{amount}}";
        
        // Format the money value
        let value = (cents / 100).toFixed(2);
        
        // Replace placeholders in the format string
        let formatted = moneyFormat.replace('{{amount}}', value);
        formatted = formatted.replace('{{amount_no_decimals}}', Math.floor(cents / 100));
        formatted = formatted.replace('{{amount_with_comma_separator}}', value.replace('.', ','));
        formatted = formatted.replace('{{amount_no_decimals_with_comma_separator}}', Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','));
        
        return formatted;
    }
}

customElements.define('product-add-to-cart-sticky', ProductAddToCartSticky);
}